import { INTEGRITAS_TREASURY } from '../utils/config';
const MDS = (window as any).MDS;

/**
 * Check the status of the node.
 * @returns {Promise<IStatus>} A promise that resolves with the status object of the node.
 */
export const checkNodeStatus = (): Promise<IStatus> => {
  return new Promise((resolve, reject) => {
    MDS.cmd('status', (status: IStatus) => {
      if (status) {
        resolve(status);
      } else {
        reject('Failed to get check node status');
      }
    });
  });
};

/**
 * Get the user's balance.
 * @returns {Promise<IBalanceResponse>} A promise that resolves with the balance response object.
 */
export const userBalance = (): Promise<IBalanceResponse> => {
  return new Promise((resolve, reject) => {
    MDS.cmd('balance', (balance: IBalance) => {
      if (balance) {
        resolve(balance.response[0]);
      } else {
        reject('Failed to get balance');
      }
    });
  });
};

/**
 * Mint a NFT with the provided metadata.
 * @param {IMetaDataNFT} metaData The metadata for the NFT to be created.
 * @returns {Promise<any>} A promise that resolves with the result of the token creation.
 */
export const mintNFT = async (metaData: IMetaDataNFT) => {
  const stringMetaData = JSON.stringify(metaData);

  return new Promise((resolve, reject) => {
    MDS.cmd(`tokencreate name:${stringMetaData} amount:1 decimals:0`, (res) => {
      if (res) {
        resolve(res);
      } else if (res.error && !res.pending) {
        console.error(`Failed to send transaction, error: ${res.error}`);
        reject('Failed to send transaction');
      }
    });
  });
};

/**
 * Get metadata for a specific NFT using its coin ID.
 * @param {string} coinId The coin ID of the NFT to retrieve metadata for.
 * @returns {Promise<string>} A promise that resolves with the token ID of the NFT.
 */
export const getNFTmetadata = (coinId: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    MDS.cmd(`tokens tokenid:${coinId}`, function (res) {
      if (res) {
        resolve(res.response[0].tokenid);
      }
      if (res.error && !res.pending) {
        console.error(`Error getting coinId: ${res.error}`);
        reject('Error getting coinId');
      }
    });
  });
};

/**
 * Initiates a transaction by sending a specified amount to the INTEGRITAS_TREASURY address
 * and mints an NFT for each file provided as proof of validation. The function returns
 * a promise that resolves with the transaction response if successful or rejects if
 * there is an error.
 *
 * @param {number} amount - The amount to send in the transaction.
 * @param {CustomFile[]} files - An array of file objects, each containing file name and hash.
 * @returns {Promise<ITransactionResponse>} - Resolves with the transaction response or rejects with an error.
 */
export const createTxn = (amount: number, files: CustomFile[]): Promise<ITransactionResponse> => {
  return new Promise((resolve, reject) => {
    MDS.cmd(`send address:${INTEGRITAS_TREASURY} amount:${amount}`, (res: ITransactionResponse) => {
      if (res) {
        //console.log('SEND TRANSACTION res: ', res);
        resolve(res);
      } else {
        reject('Failed to send transaction');
      }
    });
  });
};

/**
 * Get the token ID for a specific coin ID.
 * @param {string} coinId The coin ID for which to fetch the token ID.
 * @returns {Promise<string>} A promise that resolves with the token ID.
 */
export const getTokenId = (coinId: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    MDS.cmd(`coins coinid:${coinId}`, function (res) {
      if (res) {
        if (res.response.length > 0) {
          //console.log('Get coin id:', res.response[0].tokenid);
          resolve(res);
        }
        resolve(res);
      }
      if (res.error && !res.pending) {
        console.error(`Error getting coinId: ${res.error}`);
        reject('Error getting coinId');
      }
    });
  });
};

export const getTxPoW = (coinId: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    MDS.cmd(`history max:1`, function (res) {
      if (res) {
        // Check that coinId exist
        if (res.response.txpows[0].body.txn.outputs.some((output) => output.coinid === coinId)) {
          // console.log('Coin id is a match in history check');

          resolve(res.response.txpows[0].txpowid);
        } else {
          reject('Error getting history');
        }
      }
      if (res.error && !res.pending) {
        console.error(`Error getting history: ${res.error}`);
        reject('Error getting history');
      }
    });
  });
};

/**
 * Get the hash of a file from the Veritas file system using the file name and hash.
 * @param {string} fileName The name of the file.
 * @param {string} fileHash The hash of the file.
 * @returns {Promise<IDataHash>} A promise that resolves with the file hash data.
 */
export const hashFile = (fileName: string, fileHash: string): Promise<IDataHash> => {
  return new Promise((resolve, reject) => {
    let cpath = 'integritas/' + fileHash + '/' + fileName; // Construct the file path

    // Send a command to MDS to get the hash of the file
    MDS.file.getpath(cpath, function (pathres) {
      const fpath = pathres.response.getpath.path;

      MDS.cmd(`hash file:"${fpath}"`, (res) => {
        if (res) {
          resolve(res);
        } else {
          reject('Failed to get hash file');
        }
      });
    });
  });
};

/**
 * Hash a given file data (Uint8Array) and return the hash.
 * @param {Uint8Array} fileData The file data to be hashed.
 * @returns {Promise<IDataHash>} A promise that resolves with the file hash.
 */
export const hashData = (fileData: Uint8Array): Promise<IDataHash> => {
  return new Promise((resolve, reject) => {
    // Convert Uint8Array to hex string without 0x prefix
    const hexString = Array.from(fileData)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    MDS.cmd(`hash data:0x${hexString}`, (res) => {
      if (res) {
        resolve(res);
      } else {
        reject('Failed to get hash file');
      }
    });
  });
};

/**
 * Get a file from MDS using its hash and return the file as a blob.
 * @param {string} hash The hash of the file to retrieve.
 * @returns {Promise<any>} A promise that resolves with the file data as a blob.
 */
export const getFile = (hash: string): any => {
  const folderPath = 'integritas/' + hash;

  return new Promise((resolve, reject) => {
    MDS.file.list(folderPath, function (res) {
      if (res.status) {
        const fname = res.response.list[0].name;
        const fpath = folderPath + '/' + fname;

        MDS.file.load(fpath, (res) => {
          const blob = new Blob([res.response.load.data], { type: 'text/plain' });
          const file = { name: res.response.load.name, blob: blob };
          resolve(file);
        });
      } else {
        reject('Error listing files:');
      }
    });
  });
};

/**
 * Upload a file to MDS and return the hash of the uploaded file.
 * @param {File} file The file to upload.
 * @returns {Promise<string>} A promise that resolves with the file hash.
 */
export const uploadFile = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    MDS.log('Started upload file');
    MDS.file.upload(file, async function (res: UploadResponse) {
      if (!res) {
        reject('Failed to get status');
        return;
      }

      // Check if the file is fully uploaded
      if (res.chunk === res.allchunks) {
        const filename = res.filename;
        const cpath = 'fileupload/' + filename;

        try {
          const pathres = await new Promise((resolvePath, rejectPath) => {
            MDS.file.getpath(cpath, function (pathres) {
              pathres ? resolvePath(pathres) : rejectPath('Failed to get file path');
            });
          });

          const fpath = pathres.response.getpath.path;

          const hashRes = await new Promise((resolveHash, rejectHash) => {
            MDS.cmd(`hash file:"${fpath}"`, function (res) {
              res.response.hash ? resolveHash(res.response.hash) : rejectHash('Failed to get hash');
            });
          });
          const folder = hashRes;
          const npath = `integritas/${folder}/${filename}`;

          await new Promise((resolveMove) => {
            MDS.log('Moving file');
            MDS.file.move(cpath, npath, function () {
              resolveMove(true);
            });
          });

          resolve(hashRes);
        } catch (error) {
          console.error('Error:', error);
          reject(error);
        }
      }
    });
  });
};

/**
 * Delete a file from the Veritas folder in MDS.
 * @param {string} fileHash The hash of the file to delete.
 * @param {string} fileName The name of the file to delete.
 * @returns {Promise<boolean>} A promise that resolves with true if deletion was successful, false otherwise.
 */
export const deleteFile = (fileHash: string, fileName: string): Promise<boolean> => {
  //console.log('DELETEING FILES');

  return new Promise((resolve, reject) => {
    const filePath = `integritas/${fileHash}`;

    MDS.file.delete(filePath, function (response) {
      if (response.status) {
        // console.log(`File ${fileName} deleted successfully`);
        resolve(true);
      } else {
        console.error(`Failed to delete file ${fileName}:`, response.error);
        resolve(false);
      }
    });
  });
};
